import { Avatar, Button, Dropdown, Layout, Space, Badge, Menu, Breadcrumb, Divider, Skeleton, Tooltip, Typography, InputRef, Input, Select } from "antd";
import { useEffect, useMemo, useState, useRef } from "react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { getEnvironmentService, getNotificationService, getProjectService, isAIServiceEnabled } from "../../backend";

import Logo from "../../assets/logo-white.svg";
import { useAuth } from "../../contexts/Auth";
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { currentProjectState, projectsState } from "../../recoil/project";
import { Bot } from "lucide-react";
import {
    LayoutGrid,
    GitBranch,
    Package,
    Activity,
    DollarSign,
    Plug,
    Settings,
    FileText,
    Bell,
    Home,
    BellRing,
    AlertTriangle,
    Sparkles,
    User,
    Crown,
    Search,
    PlusCircle,
    GitMerge
} from "lucide-react";
import { useCurrentProject } from "../../contexts/Project";
import { handleSetMixpanelUserGroups, handleTrackLogoutEvent } from "../../backend/tracking";
import { PlanTag, isPaidPlan } from "../../components/PlanTag";
import ChangelogDrawer from "./ChangelogDrawer";
import { trackNotificationsClicked, trackWhatsNewClicked } from "../../backend/tracking/user-settings";
import NotificationsDrawer from "./NotificationsDrawer";
import { newNotificationsState } from "../../recoil/notification";
import { UserNotificationsNotifications } from "@microtica/ms-notification-sdk";
import { ItemType } from "antd/es/menu/interface";
import { BreadcrumbItemType } from "antd/es/breadcrumb/Breadcrumb";

const { Header, Content, Sider } = Layout;

const PageLayout = () => {
    const { Text } = Typography;
    const badgeColor = "#1890ff";
    const { projectId, envId } = useParams() as { projectId: string; envId: string; componentId: string; namespace: string; appName: string; };
    const { pathname } = useLocation()
    const navigate = useNavigate();
    const { profile } = useAuth();
    const { updateCurrentProject, paymentMethodRequired, trialDaysLeft, isProjectActive } = useCurrentProject();
    // recoil project
    const [projects, setProjects] = useRecoilState(projectsState);
    const resetProjectsState = useResetRecoilState(projectsState);
    const currentProject = useRecoilValue(currentProjectState);
    const [envName, setEnvName] = useState<string>();
    // recoil notification
    const setNewUserNotifications = useSetRecoilState(newNotificationsState);
    // useState
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [showChangelog, setShowChangelog] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showAlarmNotifications, setShowAlarmNotifications] = useState(false);
    const [userNotifications, setUserNotifications] = useState([] as UserNotificationsNotifications[]);

    const [selectedMenuItem, setSelectedMenuItem] = useState<string>("environments");

    const loadData = async () => {
        setLoadingProjects(true);
        try {
            const { data: { projects } } = await getProjectService().listProjects();
            setProjects(projects || []);
            handleSetMixpanelUserGroups(projects.map(item => item.id));
        } catch (error) {
        } finally {
            setLoadingProjects(false);
        }
    }

    useEffect(() => {
        loadData();
        fetchNotifications();

        const notificationsInterval = setInterval(async () => {
            await fetchNotifications();
        }, 30000);

        return () => {
            clearInterval(notificationsInterval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async () => {
        const { data: { name } } = await getEnvironmentService().getStage(envId, projectId);
        setEnvName(name);
    }

    useEffect(() => {
        if (envId) {
            load();
        }
    }, [envId]);

    useEffect(() => {
        if (/^\/projects\/([^\/]+)\/environments(?:\/.*)?$/.test(pathname)) {
            setSelectedMenuItem("environments");
        } else if (/^\/projects\/([^\/]+)\/templates(?:\/.*)?$/.test(pathname)) {
            setSelectedMenuItem("templates");
        } else if (/^\/projects\/([^\/]+)\/settings(?:\/.*)?$/.test(pathname)) {
            setSelectedMenuItem("settings");
        } else if (/^\/projects\/([^\/]+)\/integrations(?:\/.*)?$/.test(pathname)) {
            setSelectedMenuItem("integrations");
        } else if (/^\/projects\/([^\/]+)\/pipelines(?:\/.*)?$/.test(pathname)) {
            setSelectedMenuItem("pipelines");
        } else if (/^\/projects\/([^\/]+)\/cost-explorer(?:\/.*)?$/.test(pathname)) {
            setSelectedMenuItem("cost-explorer");
        } else if (/^\/projects\/([^\/].+)\/alarm(?:\/.*)?$/.test(pathname)) {
            setSelectedMenuItem("alarms");
        } else if (/^\/projects\/([^\/].+)\/alarms(?:\/.*)?$/.test(pathname)) {
            setSelectedMenuItem("alarms");
        } else if (/^\/projects\/([^\/].+)\/monitoring(?:\/.*)?$/.test(pathname)) {
            setSelectedMenuItem("monitoring");
        } else {
            setSelectedMenuItem("");
        }
    }, [pathname]);

    const handleSelectedProject = async (projectId: string) => {
        await updateCurrentProject(projectId);

        (window as Window).location = `/projects/${projectId}/environments`
    }

    const handleLogout = () => {
        localStorage.removeItem('idToken');
        localStorage.removeItem('expirationDate');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('profile');

        resetProjectsState();

        handleTrackLogoutEvent();

        navigate("/auth/login");
    }

    const handleOpenChangelog = () => {
        setShowChangelog(true);
        trackWhatsNewClicked();
    }

    const handleOpenNotifications = () => {
        setShowNotifications(true);
        trackNotificationsClicked();
    }

    const handleOpenAlarmNotifications = () => {
        setShowAlarmNotifications(true);
    }

    const fetchNotifications = async () => {
        const { data: { notifications } } = await getNotificationService().getAllUserNotifications();

        // update recoil state only if there are new notifications
        setUserNotifications(oldNotifications => {
            if ((oldNotifications.length === 0 && notifications.length > 0) ||
                (notifications[0]?.id !== oldNotifications[0]?.id || notifications.filter(n => n.isRead).length !== oldNotifications.filter(o => o.isRead).length)) {
                const oldNotificationsIds = oldNotifications.map(oldNotification => oldNotification.id);
                const newNotifications = notifications.filter(notification => !oldNotificationsIds.includes(notification.id));
                setNewUserNotifications(newNotifications);
                return notifications;
            } else {
                return oldNotifications;
            }
        })
    }

    const MainMenu = useMemo(() => (
        <>
            {/* Build & Deploy */}
            <div className="sidebar-section-label">Build & Deploy</div>
            <Menu
                theme="dark"
                selectedKeys={[selectedMenuItem]}
                mode="inline"
                inlineIndent={12}
                items={[
                    {
                        key: "environments",
                        label: <Link to={`/projects/${projectId}/environments`}>Environments</Link>,
                        icon: <LayoutGrid size={16} />
                    },
                    {
                        key: "pipelines",
                        label: <Link to={`/projects/${projectId}/pipelines`}>Pipelines</Link>,
                        icon: <GitMerge size={16} />
                    },
                    {
                        key: "templates",
                        label: <Link to={`/projects/${projectId}/templates`}>Templates</Link>,
                        icon: <Package size={16} />
                    }
                ]}
            />

            {/* Observe */}
            <div className="sidebar-section-label">Observe</div>
            <Menu
                theme="dark"
                selectedKeys={[selectedMenuItem]}
                mode="inline"
                inlineIndent={12}
                items={[
                    {
                        key: "monitoring",
                        label: <Link to={`/projects/${projectId}/monitoring`}>Monitoring</Link>,
                        icon: <Activity size={16} />
                    },
                    {
                        key: "alarms",
                        label: <Link to={`/projects/${projectId}/monitoring/alarms`}>Alarms</Link>,
                        icon: <Bell size={16} />
                    },
                    {
                        key: "cost-explorer",
                        label: <Link to={`/projects/${projectId}/cost-explorer`}>Cost Explorer</Link>,
                        icon: <DollarSign size={16} />
                    }
                ]}
            />

            {/* Configure */}
            <div className="sidebar-section-label">Configure</div>
            <Menu
                theme="dark"
                selectedKeys={[selectedMenuItem]}
                mode="inline"
                inlineIndent={12}
                items={[
                    {
                        key: "integrations",
                        label: <Link to={`/projects/${projectId}/integrations`}>Integrations</Link>,
                        icon: <Plug size={16} />
                    },
                    {
                        key: "settings",
                        label: <Link to={`/projects/${projectId}/settings`}>Settings</Link>,
                        icon: <Settings size={16} />
                    }
                ]}
            />
        </>
    ), [selectedMenuItem, projectId]);

    const BottomMenu = () => (
        <Menu
            theme="dark"
            selectedKeys={[selectedMenuItem]}
            defaultSelectedKeys={['environments']}
            items={[
                {
                    key: "docs",
                    label: <a href="https://docs.microtica.com" target="_blank" rel="noreferrer">
                        Documentation
                    </a>,
                    icon: <FileText size={16} />
                }
            ]} />
    )

    const ProjectsDropdown = () => {
        const [searchQuery, setSearchQuery] = useState("");
        const [dropdownOpen, setDropdownOpen] = useState(false);
        const searchInputRef = useRef<InputRef>(null);

        const filteredProjects = useMemo(() => {
            if (!searchQuery.trim()) {
                return projects;
            }
            const query = searchQuery.toLowerCase();
            return projects.filter(project => 
                project.name.toLowerCase().includes(query)
            );
        }, [projects, searchQuery]);

        const selectOptions = useMemo(() => {
            return filteredProjects.map(project => ({
                value: project.id,
                label: (
                    <div>
                        <div>
                            <Text ellipsis={{ tooltip: project.name }}>{project.name}</Text>
                        </div>
                        <div style={{ marginTop: 4 }}>
                            <PlanTag 
                                planId={project.paymentPlan?.id || 'FREE'} 
                                planName={project.paymentPlan?.name || 'Free'} 
                                compact 
                            />
                        </div>
                    </div>
                )
            }));
        }, [filteredProjects]);

        useEffect(() => {
            if (dropdownOpen && projects.length > 5 && searchInputRef.current) {
                // Small delay to ensure dropdown is fully rendered
                setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 100);
            }
        }, [dropdownOpen, projects.length]);

        return (
            <div className="projects-button">
                <Select
                    value={currentProject?.id}
                    open={dropdownOpen}
                    onDropdownVisibleChange={(open) => {
                        setDropdownOpen(open);
                        if (!open) {
                            setSearchQuery("");
                        }
                    }}
                    onChange={(value) => {
                        if (value === "new-project") {
                            // Navigate handled by the Link component
                            return;
                        }
                        handleSelectedProject(value);
                        setDropdownOpen(false);
                        setSearchQuery("");
                    }}
                    popupMatchSelectWidth={false}
                    showArrow={true}
                    labelRender={() => (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {isPaidPlan(currentProject?.paymentPlan?.id) && (
                                <Crown size={12} style={{ color: '#FCD34D', flexShrink: 0 }} />
                            )}
                            <Text ellipsis={{ tooltip: currentProject?.name }} style={{ color: "inherit" }}>
                                {currentProject?.name}
                            </Text>
                        </span>
                    )}
                    popupRender={(menu) => (
                        <>
                            {projects.length > 5 && (
                                <div 
                                    style={{ padding: '8px 12px' }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                >
                                    <Input
                                        ref={searchInputRef}
                                        placeholder="Search projects..."
                                        prefix={<Search size={14} style={{ color: '#9CA3AF' }} />}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        allowClear
                                        size="middle"
                                    />
                                </div>
                            )}
                            {menu}
                            <Divider style={{ margin: '4px 0' }} />
                            <div 
                                style={{ padding: '4px 12px 8px' }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            >
                                <Link to="/pricing/new" onClick={() => setDropdownOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <PlusCircle size={16} /> Create Project
                                </Link>
                            </div>
                        </>
                    )}
                    options={selectOptions}
                    className="projects-select"
                    style={{ width: '100%' }}
                />
            </div>
        );
    }

    const HeaderBreadcrumb = () => {
        const items: BreadcrumbItemType[] = [];
        
        // Always show project name as first breadcrumb item
        if (projectId && currentProject?.name) {
            items.push({
                key: "projectName",
                title: currentProject.name,
                onClick: () => navigate(`/projects/${currentProject.id}/environments`)
            });
        }
        if (envId) {
            items.push({
                key: "envName",
                title: !envName ? <Skeleton.Input size="small" active style={{ width: 80 }} /> : envName,
                onClick: () => navigate(`/projects/${currentProject.id}/environments/${envId}`)
            });
        }
        if (pathname.split("/").includes("alarms")) {
            items.push({
                key: "alarms",
                title: "Alarms"
            });
        }
        
        return (
            <div className="header-nav">
                <Tooltip title="All Projects">
                    <Button 
                        type="text"
                        icon={<Home size={16} />}
                        onClick={() => navigate("/projects", { state: { showProjectsList: true } })}
                        className="header-home-btn"
                    />
                </Tooltip>
                {items.length > 0 && (
                    <Breadcrumb 
                        items={items}
                        className="header-breadcrumb"
                    />
                )}
            </div>
        );
    }

    const HeaderActions = useMemo(() => {
        const updatesCount = userNotifications.filter(notification => !notification.isRead && notification.entityType !== "MONITORING").length;
        const incidentsCount = userNotifications.filter(notification => !notification.isRead && notification.entityType === "MONITORING").length;
        
        return (
            <div className="header-actions">
                <div className="header-actions-group">
                    <Tooltip title="Updates" placement="bottom">
                        <Badge count={updatesCount} size="small" offset={[-2, 2]}>
                            <button
                                onClick={handleOpenNotifications}
                                className="header-icon-btn"
                            >
                                <BellRing size={18} />
                            </button>
                        </Badge>
                    </Tooltip>
                    <Tooltip title="Incidents" placement="bottom">
                        <Badge count={incidentsCount} size="small" offset={[-2, 2]}>
                            <button
                                onClick={handleOpenAlarmNotifications}
                                className="header-icon-btn"
                            >
                                <AlertTriangle size={18} />
                            </button>
                        </Badge>
                    </Tooltip>
                    <Tooltip title="What's new" placement="bottom">
                        <button
                            onClick={handleOpenChangelog}
                            className="header-icon-btn"
                        >
                            <Sparkles size={18} />
                        </button>
                    </Tooltip>
                </div>
                <Dropdown
                    placement="bottomRight"
                    menu={{
                        items: [{
                            key: "profile-email",
                            label: (
                                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                                    {profile?.email}
                                </Typography.Text>
                            ),
                            disabled: true
                        },
                        {
                            type: 'divider'
                        },
                        {
                            key: "logout",
                            label: "Logout",
                            onClick: handleLogout
                        }]
                    }}
                    trigger={['click']}
                >
                    <div className="header-user">
                        <Avatar 
                            size={32}
                            icon={<User size={16} />}
                            className="header-avatar"
                        />
                    </div>
                </Dropdown>
            </div>
        )
    }, [userNotifications, profile?.email]);

    const ProjectInfo = () => (
        <>
            {
                isProjectActive &&
                paymentMethodRequired &&
                trialDaysLeft && trialDaysLeft > 0 &&
                <div className="info-box">
                    <Space direction="vertical">
                        <Tooltip title={`You still don't have a payment method added. Add a payment method to be able to use the project after the trial ends.`}>
                            <div style={{ color: "white" }}>
                                Free Trial ends in {trialDaysLeft} days!
                            </div>
                        </Tooltip>
                        <Link to={`projects/${projectId}/settings/billing`}>Add Payment Method</Link>
                    </Space>
                </div>
            }
            {
                (!!currentProject && !isProjectActive) ?
                    <div className="info-box">
                        <Space direction="vertical">
                            <Tooltip title="Your project is currently in an inactive state. Upgrade the project plan to continue using the project.">
                                <div style={{ color: "white" }}>
                                    Your Free Trial ended.
                                </div>
                            </Tooltip>
                            <Link to="pricing/upgrade"> Upgrade now!</Link>
                        </Space>
                    </div>
                    : null
            }
        </>
    )

    return (
        <Layout hasSider>
            <Sider
                style={{
                    overflow: 'auto',
                    height: '100vh',
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    bottom: 0
                }}
                collapsedWidth="65px"
                width={220}
            >
                <div
                    style={{ 
                        display: "flex",
                        flexDirection: "column", 
                        height: "100%" 
                    }}
                >
                    <div className="main-menu">
                        {/* Logo */}
                        <div className="sidebar-logo">
                            <Link to="/projects">
                                <img src={Logo} alt="Microtica" />
                            </Link>
                        </div>

                        {/* Project Selector */}
                        <ProjectsDropdown />

                        {/* Main Navigation */}
                        {MainMenu}

                        {/* Trial/Inactive Warning */}
                        <ProjectInfo />
                    </div>

                    {/* Bottom Section */}
                    <div className="sidebar-bottom">
                        <BottomMenu />
                    </div>
                </div>
            </Sider>
            <Layout style={{ marginLeft: 220 }}>
                <Header className="header">
                    <HeaderBreadcrumb />
                    {HeaderActions}
                </Header>
                <Content style={{ overflow: 'initial' }}>
                    <Outlet />
                </Content>
            </Layout>
            <ChangelogDrawer visible={showChangelog} onClose={() => setShowChangelog(false)} />
            {
                (showNotifications || showAlarmNotifications) &&
                <NotificationsDrawer
                    projects={projects}
                    notifications={userNotifications.filter(notification => showAlarmNotifications ? notification.entityType === "MONITORING" : notification.entityType !== "MONITORING")}
                    visible={showNotifications || showAlarmNotifications}
                    type={showAlarmNotifications ? "Incidents" : "Updates"}
                    onClose={() => {
                        setShowNotifications(false);
                        setShowAlarmNotifications(false);
                        fetchNotifications();
                    }}
                />
            }
        </Layout>
    );
}

export default PageLayout;